import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Dropdown, Drawer, Space, Badge, Menu } from 'antd';
import {
    MenuOutlined,
    GlobalOutlined,
    BulbOutlined,
    BulbFilled,
    UserOutlined,
    DashboardOutlined,
    LogoutOutlined,
    ShoppingCartOutlined,
    HomeOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';

import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { LANGUAGES } from '../lib/i18n';
import { ROUTES } from '../constants/routes';

export default function Navbar({ variant = 'public', onOpenSidebar }) {
    const { isDark, locale, setLocale, toggleMode } = useTheme();
    const { isAuthenticated, user, signOut } = useAuth();
    const { count } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();

    const [mobileOpen, setMobileOpen] = useState(false);

    const currentLang = LANGUAGES.find((l) => l.code === locale) || LANGUAGES[0];

    const handleSignOut = async () => {
        await signOut();
        navigate(ROUTES.LOGIN);
    };

    const languageMenu = {
        items: LANGUAGES.map((l) => ({
            key: l.code,
            label: l.nativeLabel,
            onClick: () => setLocale(l.code),
        })),
    };

    const userMenu = {
        items: [
            ...(variant !== 'admin' ? [
                { key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard', onClick: () => navigate(ROUTES.DASHBOARD) },
                { key: 'profile', icon: <UserOutlined />, label: 'Profile', onClick: () => navigate(ROUTES.PROFILE) },
            ] : [
                { key: 'home', icon: <HomeOutlined />, label: 'Back to App', onClick: () => navigate(ROUTES.DASHBOARD) },
            ]),
            { type: 'divider' },
            { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true, onClick: handleSignOut },
        ],
    };

    const links = {
        public: [
            { to: ROUTES.MARKET, label: 'Market' },
            { to: ROUTES.ABOUT, label: 'About' },
            { to: ROUTES.CONTACT, label: 'Contact' },
            { to: ROUTES.HELP, label: 'Help' },
        ],
        user: [
            { to: ROUTES.DASHBOARD, label: 'Dashboard' },
            { to: ROUTES.MARKET, label: 'Market' },
            { to: ROUTES.WALLETS, label: 'Wallets' },
            { to: ROUTES.VOUCHERS_LIST, label: 'Vouchers' },
        ],
        admin: [
            { to: ROUTES.ADMIN, label: 'Dashboard' },
            { to: ROUTES.ADMIN_ORDERS, label: 'Orders' },
        ],
    };

    const currentLinks = links[variant] || links.public;

    return (
        <>
            <nav className="navbar">
                <div className="navbar__inner">

                    {/* Left side */}
                    <div className="navbar__left">
                        {/* Admin Mobile Toggle */}
                        {variant === 'admin' && (
                            <button
                                type="button"
                                className="navbar__mobile-toggle"
                                onClick={onOpenSidebar}
                            >
                                <MenuOutlined />
                            </button>
                        )}

                        <Link to={variant === 'admin' ? ROUTES.ADMIN : '/'} className="navbar__brand">
                            {variant === 'admin' && (
                                <span style={{ fontSize: 10, marginLeft: 8, padding: '2px 6px', background: 'var(--home-accent)', color: '#fff', borderRadius: 4 }}>
                                    ADMIN
                                </span>
                            )}
                        </Link>

                        <div className="navbar__menu">
                            {currentLinks.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className={location.pathname.startsWith(link.to) ? 'is-active' : ''}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Right side */}
                    <div className="navbar__right">
                        <Dropdown menu={languageMenu} trigger={['click']} placement="bottomRight">
                            <button type="button" className="navbar__btn">
                                <GlobalOutlined />
                                <span className="navbar__lang-text">{currentLang.code.toUpperCase()}</span>
                            </button>
                        </Dropdown>

                        <button type="button" className="navbar__btn" onClick={toggleMode}>
                            {isDark ? <BulbFilled /> : <BulbOutlined />}
                        </button>

                        {variant === 'user' && (
                            <Badge count={count} size="small" offset={[-4, 4]}>
                                <button type="button" className="navbar__btn" onClick={() => navigate(ROUTES.CART)}>
                                    <ShoppingCartOutlined />
                                </button>
                            </Badge>
                        )}

                        {isAuthenticated ? (
                            <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
                                <Button type="primary" shape="round" className="navbar__auth-btn">
                                    {user?.user_metadata?.name?.split(' ')[0] || 'Account'}
                                </Button>
                            </Dropdown>
                        ) : (
                            variant !== 'admin' && (
                                <Space size={8}>
                                    <Button onClick={() => navigate(ROUTES.LOGIN)}>Sign In</Button>
                                    <Button type="primary" onClick={() => navigate(ROUTES.REGISTER)}>Sign Up</Button>
                                </Space>
                            )
                        )}

                        {variant !== 'admin' && (
                            <button type="button" className="navbar__mobile-toggle" onClick={() => setMobileOpen(true)}>
                                <MenuOutlined />
                            </button>
                        )}
                    </div>
                </div>
            </nav>

            {/* Mobile Drawer (Public/User only) */}
            {variant !== 'admin' && (
                <Drawer
                    placement={locale === 'fa' ? 'right' : 'left'}
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    width={280}
                    className="navbar__drawer"
                >
                    <div className="navbar__brand" style={{ marginBottom: 24 }}>
                        SOHO<span>CARD</span>
                    </div>
                    <Menu mode="inline" selectedKeys={[location.pathname]} style={{ border: 'none', background: 'transparent' }}>
                        {currentLinks.map((link) => (
                            <Menu.Item key={link.to} onClick={() => { navigate(link.to); setMobileOpen(false); }}>
                                <Link to={link.to}>{link.label}</Link>
                            </Menu.Item>
                        ))}
                    </Menu>
                </Drawer>
            )}
        </>
    );
}